import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useEventStore } from '@/stores/eventStore';
import { useCartStore } from '@/stores/cartStore';
import api from '@/services/api';

const THEMES = {
  quemimo: { '--event-primary': '#ff50a3', '--event-primary-light': '#fff0f6', '--event-primary-shadow': 'rgba(255, 80, 163, 0.35)', '--event-bg': '#f8f9fa' },
  bebe:    { '--event-primary': '#5b9bd5', '--event-primary-light': '#e8f1fb', '--event-primary-shadow': 'rgba(91, 155, 213, 0.35)',  '--event-bg': '#f8f9fa' },
  neutro:  { '--event-primary': '#6b7280', '--event-primary-light': '#f3f4f6', '--event-primary-shadow': 'rgba(107, 114, 128, 0.35)', '--event-bg': '#f8f9fa' },
};

function hexToColorVars(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    light: `rgba(${r}, ${g}, ${b}, 0.08)`,
    shadow: `rgba(${r}, ${g}, ${b}, 0.35)`,
  };
}

function applyPremiumTheme(color) {
  const { light, shadow } = hexToColorVars(color);
  document.documentElement.style.setProperty('--event-primary', color);
  document.documentElement.style.setProperty('--event-primary-light', light);
  document.documentElement.style.setProperty('--event-primary-shadow', shadow);
  document.documentElement.style.setProperty('--event-bg', '#fff');
}

function applyPremiumBg(bgImageURL, bgOpacity) {
  if (bgImageURL) {
    const overlay = `rgba(255,255,255,${(1 - bgOpacity).toFixed(2)})`;
    document.body.style.backgroundImage = `linear-gradient(${overlay}, ${overlay}), url("${bgImageURL}")`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    const area = document.querySelector('.main-content-area');
    if (area) area.style.backgroundColor = 'transparent';
  }
}

function applyThemeToRoot(theme) {
  const t = THEMES[theme] || THEMES.quemimo;
  document.documentElement.style.setProperty('--event-primary', t['--event-primary']);
  document.documentElement.style.setProperty('--event-primary-light', t['--event-primary-light']);
  document.documentElement.style.setProperty('--event-primary-shadow', t['--event-primary-shadow']);
  document.documentElement.style.setProperty('--event-bg', t['--event-bg']);
}

export function removeThemeFromRoot() {
  document.documentElement.style.removeProperty('--event-primary');
  document.documentElement.style.removeProperty('--event-primary-light');
  document.documentElement.style.removeProperty('--event-primary-shadow');
  document.documentElement.style.removeProperty('--event-bg');
  document.body.style.backgroundImage = '';
  document.body.style.backgroundSize = '';
  document.body.style.backgroundPosition = '';
  document.body.style.backgroundAttachment = '';
  const area = document.querySelector('.main-content-area');
  if (area) area.style.backgroundColor = '';
}

/**
 * Composable que aplica o tema do evento no :root.
 * Lê do eventStore (navegação em sessão) ou busca na API (acesso direto à URL).
 * @param {string|Ref<string>} eventId — ID do evento
 * @param {object} options
 * @param {boolean} options.cleanup — se deve remover as vars ao desmontar (default: false)
 */
export function useEventTheme(eventId, { cleanup = false } = {}) {
  const eventStore = useEventStore();
  const cartStore = useCartStore();
  const isReady = ref(false);

  async function resolveAndApply() {
    const id = typeof eventId === 'object' ? eventId.value : eventId;
    if (!id) { isReady.value = true; return; }

    // 1. Usa o tema do store se disponível (navegação em sessão)
    if (eventStore.theme) {
      if (eventStore.eventData?.id && String(eventStore.eventData.id) === String(id)) {
        cartStore.hydrateEvent(eventStore.eventData);
      }
      if (eventStore.theme === 'premium' && eventStore.customColor) {
        applyPremiumTheme(eventStore.customColor);
        applyPremiumBg(eventStore.bgImageURL, eventStore.bgOpacity ?? 0.95);
      } else if (THEMES[eventStore.theme]) {
        applyThemeToRoot(eventStore.theme);
      }
      isReady.value = true;
      return;
    }

    // 2. Fallback: busca o tema da API (acesso direto à URL)
    try {
      const { data } = await api.get(`/events/${id}`);
      cartStore.hydrateEvent(data);
      const theme = data?.customization?.theme;
      const cust = data?.customization;
      if (theme === 'premium' && cust?.is_premium) {
        const color = cust.primary_color || '#ff50a3';
        applyPremiumTheme(color);
        applyPremiumBg(cust.background_image_url, cust.background_opacity ?? 0.95);
        eventStore.setTheme('premium');
        eventStore.setCustomization({
          customColor: color,
          bgImageURL:  cust.background_image_url || null,
          bgOpacity:   cust.background_opacity ?? 0.95,
        });
      } else if (theme && THEMES[theme]) {
        eventStore.setTheme(theme);
        applyThemeToRoot(theme);
      } else {
        applyThemeToRoot('quemimo');
      }
    } catch {
      applyThemeToRoot('quemimo');
    }
    isReady.value = true;
  }

  // Reaplica quando o store muda (ex: usuário troca tema no sidebar)
  watch(() => eventStore.theme, (theme) => {
    if (theme === 'premium' && eventStore.customColor) {
      applyPremiumTheme(eventStore.customColor);
      applyPremiumBg(eventStore.bgImageURL, eventStore.bgOpacity ?? 0.95);
    } else if (theme && THEMES[theme]) {
      applyThemeToRoot(theme);
    }
  });

  onMounted(resolveAndApply);

  if (cleanup) {
    onBeforeUnmount(removeThemeFromRoot);
  }

  return { isReady };
}
